import {
  FLIPPER_BOT_ID,
  isFlipperEnabledForViewer
} from '@dolfin/business/constants/flipper'
import { Database } from '@dolfin/business/db_types'
import { useSupabaseClient } from '@dolfin/hooks/supabase'
import { useOrgContext } from '@dolfin/hooks/state/organization'
import { t } from '@lingui/core/macro'
import { Archive, ThumbsDown, ThumbsUp } from '@untitled-ui/icons-react'
import { format, formatDistanceToNow } from 'date-fns'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import CommentBox from '../../comment-box'
import { BadgeWinCard, normalizeBadgeWonNotificationPayload } from '../badges'
import Avatar from '../../stories/Avatar'
import { Button } from '../../stories/Button'
import { IconWrapper } from '../../stories/IconWrapper'
import Switch from '../../stories/Switch'
import { getInitials } from '../../stories/utils'
import { BadgeWinShareMenu } from './BadgeWinShareMenu'
import { FlipperAvatar } from '../flipper/FlipperAvatar'
import { renderTaggedCommentContent } from './tagging'
import {
  formatPayoutReferenceText,
  formatStatementReferenceText,
  NotificationWithReferences,
  resolveNotificationPayoutReference,
  resolveNotificationStatementReference
} from './reference-labels'

function triggerFlipperAnalyze(
  issueId: number,
  organizationId: number,
  commentId?: number,
  optedOut?: boolean
) {
  const url = typeof window !== 'undefined' ? '/api/v1/flipper/analyze' : ''
  if (!url) return
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      issueId,
      organizationId,
      commentId,
      optedOut: optedOut ?? false
    })
  }).catch(() => {})
}

function submitFlipperFeedback(
  commentId: number,
  feedback: 'helpful' | 'not_helpful'
) {
  const url = typeof window !== 'undefined' ? '/api/v1/flipper/feedback' : ''
  if (!url) return
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ commentId, feedback })
  }).catch(() => {})
}

interface NotificationItemProps {
  notification: NotificationWithReferences
  writingComment: number | undefined
  setWritingComment: (writingComment: number | undefined) => void
  reloadNotifications: () => Promise<void>
  onClose?: () => void
  onArchive?: () => void
  hideGoToButton?: boolean
}

const NotificationItem = ({
  notification,
  writingComment,
  setWritingComment,
  reloadNotifications,
  onClose,
  onArchive,
  hideGoToButton
}: NotificationItemProps) => {
  const { currentUserEmail, currentUserId } = useOrgContext(state => ({
    currentUserEmail: state.profile?.email,
    currentUserId: state.profile?.id
  }))
  const supabase = useSupabaseClient<Database>()
  const router = useRouter()
  const [isHovered, setIsHovered] = useState(false)
  const [flipperOptOut, setFlipperOptOut] = useState(false)
  const [flipperFeedback, setFlipperFeedback] = useState<
    Record<number, 'helpful' | 'not_helpful'>
  >({})
  const badgeWinPayload =
    notification.type === 'badge_won'
      ? normalizeBadgeWonNotificationPayload(notification.payload)
      : null
  const isFlipperEnabled =
    notification.organization_id != null &&
    isFlipperEnabledForViewer({
      organizationId: notification.organization_id,
      email: currentUserEmail
    })

  const getDescription = useCallback(
    (notification: NotificationWithReferences) => {
      const subject = notification.profiles
        ? notification.profiles.name!
        : t`Someone`
      const payoutReference = resolveNotificationPayoutReference(notification)
      const statementReference =
        resolveNotificationStatementReference(notification)

      switch (notification.type) {
        case 'payout_flagged':
          if (notification.profile_id === currentUserId) {
            return t`You reported an issue on ${formatPayoutReferenceText(payoutReference)}`
          }
          return t`${subject} reported an issue on ${formatPayoutReferenceText(payoutReference)}`
        case 'statement_flagged':
          if (notification.profile_id === currentUserId) {
            return t`You reported an issue on ${formatStatementReferenceText(statementReference)}`
          }
          return t`${subject} reported an issue on ${formatStatementReferenceText(statementReference)}`
        case 'payout_resolved':
          if (notification.profile_id === currentUserId) {
            return t`You resolved the issue on ${formatPayoutReferenceText(payoutReference)}`
          }
          return t`${subject} resolved the issue on ${formatPayoutReferenceText(payoutReference)}`
        case 'statement_resolved':
          if (notification.profile_id === currentUserId) {
            return t`You resolved the issue on ${formatStatementReferenceText(statementReference)}`
          }
          return t`${subject} resolved the issue on ${formatStatementReferenceText(statementReference)}`
        case 'payout_modified':
          if (notification.profile_id === currentUserId) {
            return t`You modified ${formatPayoutReferenceText(payoutReference)}`
          }
          return t`${subject} modified ${formatPayoutReferenceText(payoutReference)}`
        case 'payout_paid':
          if (notification.profile_id === currentUserId) {
            return t`You marked ${formatPayoutReferenceText(payoutReference)} as paid`
          }
          return t`${subject} marked ${formatPayoutReferenceText(payoutReference)} as paid`
        case 'payout_validated':
          if (notification.profile_id === currentUserId) {
            return t`You validated ${formatPayoutReferenceText(payoutReference)}`
          }
          return t`${subject} validated ${formatPayoutReferenceText(payoutReference)}`
        case 'payout_adjustment_created':
          if (notification.profile_id === currentUserId) {
            return t`You adjusted ${formatPayoutReferenceText(payoutReference)}`
          }
          return t`${subject} adjusted ${formatPayoutReferenceText(payoutReference)}`
        case 'statement_approved':
          if (notification.profile_id === currentUserId) {
            return t`You approved ${formatStatementReferenceText(statementReference)}`
          }
          return t`${subject} approved ${formatStatementReferenceText(statementReference)}`
        case 'statement_paid':
          if (notification.profile_id === currentUserId) {
            return t`You marked ${formatStatementReferenceText(statementReference)} as paid`
          }
          return t`${subject} marked ${formatStatementReferenceText(statementReference)} as paid`
        case 'statement_approval_reverted':
          return t`Approval for ${formatStatementReferenceText(statementReference)} was reverted due to a compensation change and requires re-approval`
        case 'payout_validation_reset':
          return t`Validation for ${formatPayoutReferenceText(payoutReference)} was reset and requires re-validation`
        default:
          return ''
      }
    },
    [currentUserId]
  )

  const getGoToText = useCallback(
    (notification: NotificationWithReferences) => {
      switch (notification.type) {
        case 'payout_flagged':
        case 'payout_resolved':
        case 'payout_modified':
        case 'payout_paid':
        case 'payout_validated':
        case 'payout_adjustment_created':
        case 'statement_flagged':
        case 'statement_resolved':
        case 'statement_approved':
        case 'statement_paid':
        case 'statement_approval_reverted':
        case 'payout_validation_reset':
          return t`See details`
        default:
          return ''
      }
    },
    []
  )

  const goTo = async () => {
    if (notification.entity_type === 'payout') {
      router.push(
        `/dashboard?page=compensations&payoutId=${notification.entity_id}`
      )
      onClose?.()

      return
    }

    if (notification.entity_type === 'issue') {
      const issueId = notification.entity_id
      if (!issueId) return
      router.push(`/dashboard?page=issues&issueId=${issueId}`)
      onClose?.()
      return
    }

    if (notification.entity_type === 'statement') {
      router.push(`/dashboard?page=statements`)
      onClose?.()
      return
    }
  }

  const markAsRead = async (notificationId: number) => {
    const { error } = await supabase
      .from('notifications_status')
      .update({ status: 'read' })
      .eq('notification_id', notificationId)
      .eq('profile_id', currentUserId!)

    if (!error) {
      await reloadNotifications()
    }
  }

  const insertComment = async (
    comment: string,
    notificationId: number,
    mentions: string[]
  ) => {
    const { data: insertedComment } = await supabase
      .from('comments')
      .insert({
        content: comment,
        notification_id: notificationId,
        profile_id: currentUserId!
      })
      .select('id')

    if (insertedComment) {
      await supabase.from('comments_mentions').insert(
        mentions.map(m => ({
          comment_id: insertedComment[0].id,
          profile_id: m
        }))
      )

      if (
        notification.entity_type === 'issue' &&
        notification.entity_id != null &&
        notification.organization_id != null &&
        isFlipperEnabled
      ) {
        triggerFlipperAnalyze(
          notification.entity_id,
          notification.organization_id,
          insertedComment[0].id,
          flipperOptOut
        )
      }

      setWritingComment(undefined)
      await reloadNotifications()
    }
  }

  const onCommentCancel = () => {
    setWritingComment(undefined)
  }

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation()
    onArchive?.()
  }

  if (notification.type === 'badge_won' && badgeWinPayload) {
    const cardBody =
      badgeWinPayload.source === 'manual'
        ? t`You received this badge through a manual recognition award.`
        : t`You earned this badge through challenge performance.`

    return (
      <div
        onClick={() => markAsRead(notification.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className='flex items-start gap-3 p-4 cursor-pointer hover:bg-neutral-50 w-full border-b border-neutral-100'>
        <div className='flex-1 min-w-0'>
          <BadgeWinCard
            action={<BadgeWinShareMenu notificationId={notification.id} />}
            body={cardBody}
            compact
            headline={t`You’ve earned a new badge`}
            payload={badgeWinPayload}
          />
        </div>
        {onArchive ? (
          <div className='shrink-0 w-10 flex items-start justify-end pt-0.5'>
            <button
              onClick={handleArchive}
              className={`p-1.5 rounded-md transition-colors ${
                isHovered
                  ? 'opacity-100 hover:bg-neutral-200'
                  : 'opacity-40 hover:opacity-100'
              }`}
              title={t`Archive`}>
              <IconWrapper
                Icon={Archive}
                height={16}
                className='text-neutral-500'
              />
            </button>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      onClick={() => markAsRead(notification.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className='flex items-start gap-2 p-4 cursor-pointer hover:bg-neutral-50 w-full border-b border-neutral-100'>
      {/* Unread indicator and Avatar */}
      <div className='flex items-start gap-1 shrink-0'>
        <div
          className={`w-2 h-2 rounded-full shrink-0 mt-2 ${
            notification.notifications_status?.[0]?.status === 'unread'
              ? 'bg-highlight-500'
              : 'bg-transparent'
          }`}
        />
        {notification.profiles && (
          <Avatar
            size={'s'}
            imgUrl={notification.profiles.img_url ?? undefined}
            withTooltip={true}
            label={
              getInitials(
                notification.profiles.name ??
                  notification.profiles.email.split('@').join(' ')
              )!
            }
          />
        )}
      </div>

      {/* Content - flex-1 min-w-0 so text never overlaps the archive slot on the right */}
      <div className='flex-1 min-w-0 flex flex-col items-start justify-start text-left'>
        <p className='font-semibold break-words text-sm leading-tight'>
          {getDescription(notification)}
        </p>
        <p
          className='text-xs text-neutral-500 mt-0.5'
          title={format(
            new Date(notification.created_at),
            'MMM dd, yyyy HH:mm'
          )}>
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true
          })}
        </p>

        {/* Comments */}
        {notification.comments?.map((comment, idx) => (
          <div
            key={`comment_${comment.id}`}
            className='text-sm text-primary-800 py-2 w-full flex flex-col gap-1'>
            {(idx > 0 || comment.profile_id !== notification.profile_id) && (
              <div className='flex gap-2 justify-start items-center'>
                {comment.profile_id === FLIPPER_BOT_ID ? (
                  <span className='flex items-center gap-1.5'>
                    <FlipperAvatar size='xs' />
                    <span className='text-xs font-medium text-pink-600'>
                      Flipper AI
                    </span>
                  </span>
                ) : (
                  comment.profiles && (
                    <Avatar
                      size={'xs'}
                      name={comment.profiles.name ?? undefined}
                      imgUrl={comment.profiles.img_url ?? undefined}
                      withTooltip={true}
                      label={
                        getInitials(
                          comment.profiles.name ??
                            comment.profiles.email.split('@').join(' ')
                        )!
                      }
                    />
                  )
                )}
              </div>
            )}
            <p
              className={
                comment.profile_id === FLIPPER_BOT_ID
                  ? "break-words text-neutral-700 text-left text-sm font-normal font-['Inter'] bg-pink-50 border border-pink-100 rounded-md p-2"
                  : "break-words text-neutral-700 text-left text-sm font-normal font-['Inter'] bg-neutral-50 rounded-md p-2"
              }>
              {renderTaggedCommentContent({
                content: comment.content,
                mentionNamesById: Object.fromEntries(
                  (comment.comments_mentions ?? [])
                    .filter(cm => cm.profiles?.name)
                    .map(cm => [cm.profile_id, cm.profiles!.name!])
                )
              })}
            </p>

            <div className='flex items-center gap-1'>
              <p
                className='text-xs text-neutral-500'
                title={format(
                  new Date(comment.created_at),
                  'MMM dd, yyyy HH:mm'
                )}>
                {formatDistanceToNow(new Date(comment.created_at), {
                  addSuffix: true
                })}
              </p>

              {/* Feedback thumbs for Flipper comments (only when Flipper is enabled for this org) */}
              {comment.profile_id === FLIPPER_BOT_ID &&
                isFlipperEnabled && (
                  <span className='flex items-center gap-0.5 ml-2'>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        setFlipperFeedback(prev => ({
                          ...prev,
                          [comment.id]: 'helpful'
                        }))
                        submitFlipperFeedback(comment.id, 'helpful')
                      }}
                      className={`p-0.5 rounded transition-colors ${
                        flipperFeedback[comment.id] === 'helpful'
                          ? 'text-green-600 bg-green-50'
                          : 'text-neutral-400 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title={t`Helpful`}>
                      <ThumbsUp width={12} height={12} />
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        setFlipperFeedback(prev => ({
                          ...prev,
                          [comment.id]: 'not_helpful'
                        }))
                        submitFlipperFeedback(comment.id, 'not_helpful')
                      }}
                      className={`p-0.5 rounded transition-colors ${
                        flipperFeedback[comment.id] === 'not_helpful'
                          ? 'text-red-600 bg-red-50'
                          : 'text-neutral-400 hover:text-red-600 hover:bg-red-50'
                      }`}
                      title={t`Not helpful`}>
                      <ThumbsDown width={12} height={12} />
                    </button>
                  </span>
                )}
            </div>
          </div>
        ))}

        {/* Action buttons */}
        <div className='flex items-center justify-start w-full flex-wrap gap-2 mt-2'>
          {!writingComment &&
            notification.type !== 'payout_resolved' &&
            notification.type !== 'statement_resolved' && (
              <Button
                hierarchy='linkColor'
                size='small'
                label={
                  notification.comments && notification.comments.length > 0
                    ? t`Reply`
                    : t`Add comment`
                }
                onPress={() => setWritingComment(notification.id)}></Button>
            )}

          {!hideGoToButton &&
            (notification.entity_type === 'issue' ||
              notification.type.startsWith('payout_') ||
              notification.type.startsWith('statement_')) && (
              <Button
                hierarchy='linkColor'
                size='small'
                label={getGoToText(notification)}
                onPress={goTo}></Button>
            )}
        </div>

        {/* Comment box - FULL WIDTH */}
        {writingComment === notification.id && (
          <div className='mt-3 w-full'>
            {notification.entity_type === 'issue' &&
              isFlipperEnabled && (
                <div className='flex items-center gap-2 mb-2'>
                  <Switch
                    size='s'
                    isSelected={!flipperOptOut}
                    onChange={() => setFlipperOptOut(prev => !prev)}>
                    <Switch.Label>
                      <span className='text-xs text-neutral-600'>
                        {t`Get AI assistance from Flipper`}
                      </span>
                    </Switch.Label>
                  </Switch>
                </div>
              )}
            <CommentBox
              insertComment={(comment, mentions) =>
                insertComment(comment, notification.id, mentions)
              }
              onCancel={onCommentCancel}
            />
          </div>
        )}
      </div>

      {/* Archive button - fixed slot so text never overlaps the icon */}
      {onArchive && (
        <div className='shrink-0 w-10 flex items-start justify-end pt-0.5'>
          <button
            onClick={handleArchive}
            className={`p-1.5 rounded-md transition-colors ${
              isHovered
                ? 'opacity-100 hover:bg-neutral-200'
                : 'opacity-40 hover:opacity-100'
            }`}
            title={t`Archive`}>
            <IconWrapper
              Icon={Archive}
              height={16}
              className='text-neutral-500'
            />
          </button>
        </div>
      )}
    </div>
  )
}

export default NotificationItem
